import { useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DashboardThemeProvider, ThemeToggleButton } from "@/context/DashboardTheme";
import FulfillmentLogin from "@/pages/fulfillment/FulfillmentLogin";
import FulfillmentDashboard from "@/pages/fulfillment/FulfillmentDashboard";
import { Toaster } from "@/components/ui/sonner";
import { Package, LogOut, Loader2, Building2, Menu, X } from "lucide-react";

function FulfillmentShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (loading) return <div className="min-h-screen bg-[#0d1118] flex items-center justify-center"><Loader2 className="w-8 h-8 text-sky-400/40 animate-spin" /></div>;
  if (!user || user === false || !["fulfillment_staff", "admin"].includes(user.role)) return <FulfillmentLogin onLogin={() => navigate("/fulfillment", { replace: true })} />;

  const handleLogout = async () => { await logout(); navigate("/fulfillment", { replace: true }); };

  return (
    <div className="min-h-screen bg-paw-bg dark:bg-[#0d1118] text-paw-text dark:text-white flex" data-pawlinx-app="fulfillment" data-testid="fc-layout">
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white dark:bg-[#141822] border-b border-paw-border dark:border-white/5 flex items-center justify-between px-4">
        <button onClick={() => setOpen(true)} className="p-2 -ml-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" data-testid="fc-menu-btn"><Menu className="w-5 h-5" /></button>
        <div className="flex items-center gap-2">
          <img src="/logo-light-256.png" alt="PawLinx" className="h-9 w-auto dark:hidden" data-testid="pawlinx-logo" />
          <img src="/logo-dark-256.png" alt="PawLinx" className="h-9 w-auto hidden dark:block" data-testid="pawlinx-logo-dark" />
          <span className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Fulfillment</span>
        </div>
        <ThemeToggleButton />
      </header>

      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-[#141822] border-r border-paw-border dark:border-white/5 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="fc-sidebar">
        <div className="px-6 py-6 border-b border-paw-border dark:border-white/5 flex items-center justify-between">
          <div>
            <img src="/logo-light-256.png" alt="PawLinx" className="h-12 w-auto dark:hidden" data-testid="pawlinx-logo" />
            <img src="/logo-dark-256.png" alt="PawLinx" className="h-12 w-auto hidden dark:block" data-testid="pawlinx-logo-dark" />
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1.5 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-sky-500 dark:text-sky-400" strokeWidth={1.5} /> Fulfillment</p>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden p-1 rounded hover:bg-black/5 dark:hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          <NavLink to="/fulfillment" end onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg font-manrope text-sm transition-colors ${isActive ? "bg-sky-500/15 text-sky-600 dark:text-sky-300" : "text-paw-text-secondary dark:text-white/60 hover:text-paw-text dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5"}`} data-testid="fc-nav-orders">
            <Package className="w-4 h-4" strokeWidth={1.5} /> Active Orders
          </NavLink>
        </nav>
        <div className="p-3 border-t border-paw-border dark:border-white/5 space-y-2">
          <ThemeToggleButton className="w-full justify-start hidden lg:flex" />
          <div className="px-3 py-2">
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Signed in as</p>
            <p className="font-manrope text-sm text-paw-text dark:text-white/80 truncate">{user.first_name} {user.last_name}</p>
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-0.5">{user.email}</p>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-manrope text-paw-text-secondary dark:text-white/60 hover:text-paw-text dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 transition-colors" data-testid="fc-logout">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden pt-14 lg:pt-0">
        <Routes>
          <Route path="/" element={<FulfillmentDashboard />} />
          <Route path="/fulfillment" element={<FulfillmentDashboard />} />
          <Route path="/fulfillment/*" element={<Navigate to="/fulfillment" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function FulfillmentLayout() {
  return (
    <DashboardThemeProvider storageKey="pawlinx-fc-theme">
      <AuthProvider>
        <FulfillmentShell />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </DashboardThemeProvider>
  );
}
